import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Sparkles, Building2, ShieldCheck, BarChart3, Languages, Sun, Moon, ArrowLeft } from 'lucide-react';
import { useI18n, LANGUAGES } from '../i18n/I18nProvider';
import { useTheme } from '../theme/ThemeProvider';
import { Button, Field, Input } from '../ui';

export default function LoginPage() {
  const { t, lang, setLang } = useI18n();
  const { isDark, setPreference } = useTheme();
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: '', password: '', code: '' });
  const [needCode, setNeedCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { username, password, code } = form;
      // Сессию ставит сервер (httpOnly-cookie)
      await api.post('/admin/login', needCode ? { username, password, code } : { username, password });
      // Новый вход — без «застрявшего» просмотра чужой компании
      localStorage.removeItem('adminViewAsCompany');
      localStorage.setItem('adminSelectedTenant', 'all');
      navigate('/', { replace: true });
    } catch (err) {
      const data = err.response?.data;
      if (data?.two_factor_required && !needCode) {
        // Пароль верный, нужен код из приложения-автентификатора
        setNeedCode(true);
      } else {
        setError(needCode ? t('login.codeInvalid') : data?.error || t('login.invalid'));
      }
    } finally {
      setLoading(false);
    }
  };

  const nextLang = LANGUAGES.find((l) => l.code !== lang);

  return (
    <div className="grid min-h-full lg:grid-cols-2">
      {/* Бренд */}
      <div className="relative hidden overflow-hidden bg-brand-950 p-12 text-white lg:flex lg:flex-col lg:justify-between">
        <div className="absolute -right-24 -top-24 h-96 w-96 rounded-full bg-brand-600/30 blur-3xl" aria-hidden="true" />
        <div className="absolute -bottom-32 -left-16 h-96 w-96 rounded-full bg-indigo-400/20 blur-3xl" aria-hidden="true" />
        <div className="relative flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 ring-1 ring-white/20">
            <Sparkles className="h-4 w-4" />
          </span>
          <span className="text-sm font-semibold">{t('app.name')}</span>
        </div>
        <div className="relative max-w-md">
          <h2 className="text-3xl font-semibold leading-tight tracking-tight">{t('login.sideTitle')}</h2>
          <p className="mt-4 text-base text-brand-100/80">{t('login.sideText')}</p>
          <ul className="mt-10 space-y-4 text-sm text-brand-100/90">
            {[
              { icon: Building2, text: t('app.ownerConsole') },
              { icon: BarChart3, text: t('nav.analytics') },
              { icon: ShieldCheck, text: t('nav.audit') },
            ].map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-center gap-3">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/10"><Icon className="h-4 w-4" /></span>
                {text}
              </li>
            ))}
          </ul>
        </div>
        <p className="relative text-xs text-brand-100/50">© {new Date().getFullYear()} {t('app.name')}</p>
      </div>

      {/* Форма */}
      <div className="flex flex-col bg-white dark:bg-zinc-950">
        <div className="flex justify-end gap-1 p-4">
          <Button variant="ghost" size="sm" icon={Languages} onClick={() => setLang(nextLang.code)}>{nextLang.short}</Button>
          <Button variant="ghost" size="icon-sm" onClick={() => setPreference(isDark ? 'light' : 'dark')} aria-label={t('topbar.theme')}>
            {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
        </div>
        <div className="flex flex-1 items-center justify-center px-6 pb-16">
          <form onSubmit={submit} className="w-full max-w-sm">
            <span className="mb-6 flex h-10 w-10 items-center justify-center rounded-xl bg-brand-600 text-white shadow-sm lg:hidden">
              <Sparkles className="h-5 w-5" />
            </span>
            <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">{t('login.title')}</h1>
            <p className="mt-1.5 text-sm text-zinc-500 dark:text-zinc-400">{t('login.subtitle')}</p>

            {needCode ? (
              <div className="mt-8 space-y-4">
                <p className="flex items-start gap-2 rounded-lg bg-brand-50 px-3 py-2.5 text-sm text-brand-800 dark:bg-brand-950/40 dark:text-brand-200">
                  <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
                  {t('login.codeHint')}
                </p>
                <Field label={t('login.codeLabel')}>
                  <Input
                    value={form.code}
                    onChange={(e) => { setForm({ ...form, code: e.target.value.replace(/\D/g, '').slice(0, 6) }); setError(''); }}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                    required
                    placeholder="123456"
                    className="h-11 text-center font-mono text-lg tracking-[0.4em]"
                  />
                </Field>
                <button
                  type="button"
                  onClick={() => { setNeedCode(false); setForm({ ...form, code: '' }); setError(''); }}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200"
                >
                  <ArrowLeft className="h-3.5 w-3.5" /> {t('login.back')}
                </button>
              </div>
            ) : (
            <div className="mt-8 space-y-4">
              <Field label={t('login.usernameLabel')}>
                <Input
                  value={form.username}
                  onChange={(e) => { setForm({ ...form, username: e.target.value }); setError(''); }}
                  autoComplete="username"
                  autoFocus
                  required
                  className="h-10"
                />
              </Field>
              <Field label={t('login.passwordLabel')}>
                <Input
                  type="password"
                  value={form.password}
                  onChange={(e) => { setForm({ ...form, password: e.target.value }); setError(''); }}
                  autoComplete="current-password"
                  required
                  className="h-10"
                />
              </Field>
            </div>
            )}

            {error && (
              <p role="alert" className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/50 dark:text-red-300">
                {error}
              </p>
            )}

            <Button type="submit" size="lg" loading={loading} className="mt-6 w-full justify-center">
              {loading ? t('login.submitting') : t('login.submit')}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
